import React from "react";

const PetHealthReportPDF = ({ formData, geminiAnalysis, onClose }) => {
  // Calculate age helper function
  const calculateAge = (dob) => {
    if (!dob) return "N/A";

    const birthDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) {
      return `${years} year(s) ${months} month(s)`;
    } else if (months > 0) {
      return `${months} month(s) ${days} day(s)`;
    } else {
      return `${days} day(s)`;
    }
  };

  // Generate PDF content as HTML string
  const generatePDFContent = () => {
    const currentDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>VetSense AI Health Report - ${formData.petName || "Pet"}</title>
      <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }

    @page { 
      size: A4; 
      margin: 12mm; 
    }

    body {
      font-family: 'Arial', sans-serif;
      font-size: 11px;       /* ⬆️ increased */
      line-height: 1.45;     /* ⬆️ more breathing space */
      color: #333;
      background: white;
    }

    .report-container {
      max-width: 210mm;
      margin: 0 auto;
      background: white;
      min-height: 297mm;
      padding: 10px;        /* ⬆️ more padding */
    }

    /* --- HEADER --- */
    .header {
      background: linear-gradient(135deg, #2563eb, #1d4ed8);
      color: white;
      padding: 12px;        /* ⬆️ */
      text-align: center;
      margin-bottom: 14px;  /* ⬆️ */
      border-radius: 6px;
    }
    .header h1 { font-size: 18px; margin-bottom: 5px; }
    .header p { font-size: 10px; opacity: 0.9; }

    /* --- META INFO --- */
    .meta-info {
      display: flex; justify-content: space-between; align-items: center;
      padding: 8px 10px;    /* ⬆️ */
      background: #f8fafc; border: 1px solid #e2e8f0;
      border-radius: 6px;
      margin-bottom: 14px;  /* ⬆️ */
    }

    .priority-badge { padding: 5px 10px; border-radius: 12px; font-weight: bold; font-size: 10px; color: white; }
    .confidence-badge { padding: 4px 8px; border-radius: 10px; font-size: 9px; font-weight: 500; }

    /* --- PET INFO --- */
    .pet-info {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 10px;            /* ⬆️ */
      margin-bottom: 14px;  /* ⬆️ */
      padding: 10px;        /* ⬆️ */
      background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px;
    }
    .pet-info-label { font-weight: bold; font-size: 10px; color: #6b7280; margin-bottom: 2px; }
    .pet-info-value { font-size: 11px; color: #1f2937; }

    /* --- SECTIONS --- */
    .section { margin-bottom: 14px; break-inside: avoid; }
    .section-title { font-size: 13px; font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #e5e7eb; }

    /* --- BOXES --- */
    .assessment-box { padding: 10px; border-radius: 6px; border-left: 3px solid; margin-bottom: 12px; font-size: 11px; }

    .findings-grid, .recommendations-grid, .warning-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;            /* ⬆️ */
    }

    .finding-item, .rec-box, .warning-box, .vet-recommendation, .symptoms-summary, .additional-notes {
      padding: 8px;         /* ⬆️ */
      border-radius: 6px; 
      border: 1px solid #cbd5e1; 
      font-size: 10px;      /* ⬆️ */
    }

    .finding-title, .rec-title, .warning-title, .symptoms-title, .vet-rec-title, .notes-title {
      font-weight: bold; font-size: 11px; margin-bottom: 4px;
    }

    .finding-list, .rec-list, .warning-list { list-style: none; padding-left: 0; }
    .finding-list li, .rec-list li, .warning-list li { margin-bottom: 3px; padding-left: 12px; position: relative; font-size: 10px; }

    /* --- FOOTER --- */
    .footer { margin-top: 18px; padding-top: 10px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 9px; color: #6b7280; }
    .disclaimer { background: #fef3c7; border: 1px solid #f59e0b; padding: 8px; border-radius: 6px; margin-top: 10px; }
    .disclaimer-text { font-size: 9px; text-align: center; color: #92400e; }

    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .report-container { transform: scale(0.98); transform-origin: top center; }
    }
  </style>
</head>
<body>
    <div class="report-container">
        <div class="header">
            <h1>🤖 VetSense AI Health Analysis Report</h1>
            <p>Advanced Pet Health Assessment • Generated on ${currentDate}</p>
        </div>
        
        <div class="meta-info">
            <div class="priority-badge priority-${
              geminiAnalysis?.riskLevel || "medium"
            }">
                ${
                  geminiAnalysis?.riskLevel === "high"
                    ? "⚠️ HIGH PRIORITY"
                    : geminiAnalysis?.riskLevel === "medium"
                    ? "⚡ MEDIUM PRIORITY"
                    : "✅ LOW PRIORITY"
                }
            </div>
            <div class="confidence-badge confidence-${
              geminiAnalysis?.confidenceLevel || "medium"
            }">
                Confidence: ${(
                  geminiAnalysis?.confidenceLevel || "medium"
                ).toUpperCase()}
            </div>
        </div>
        
        <div class="pet-info">
            <div class="pet-info-item">
                <div class="pet-info-label">Pet Name</div>
                <div class="pet-info-value">${formData?.petName || "N/A"}</div>
            </div>
            <div class="pet-info-item">
                <div class="pet-info-label">Species</div>
                <div class="pet-info-value">${
                  (formData?.petType || "N/A").charAt(0).toUpperCase() +
                  (formData?.petType || "N/A").slice(1)
                }</div>
            </div>
            <div class="pet-info-item">
                <div class="pet-info-label">Breed</div>
                <div class="pet-info-value">${formData?.breed || "N/A"}</div>
            </div>
            <div class="pet-info-item">
                <div class="pet-info-label">Age</div>
                <div class="pet-info-value">${calculateAge(
                  formData?.dateOfBirth
                )}</div>
            </div>
            <div class="pet-info-item">
                <div class="pet-info-label">Sex</div>
                <div class="pet-info-value">${
                  (formData?.sex || "N/A").charAt(0).toUpperCase() +
                  (formData?.sex || "N/A").slice(1)
                }</div>
            </div>
            <div class="pet-info-item">
                <div class="pet-info-label">Weight</div>
                <div class="pet-info-value">${
                  formData?.weight ? formData.weight + " kg" : "N/A"
                }</div>
            </div>
        </div>
        
        ${
          formData?.additionalSymptoms
            ? `
        <div class="symptoms-summary">
            <div class="symptoms-title">📋 Reported Symptoms</div>
            <div class="symptoms-text">${formData.additionalSymptoms}</div>
        </div>
        `
            : ""
        }
        
        <div class="section">
            <div class="section-title">Overall Assessment</div>
            <div class="assessment-box assessment-${
              geminiAnalysis?.riskLevel || "medium"
            }">
                ${
                  geminiAnalysis?.overallAssessment ||
                  "Assessment not available"
                }
            </div>
        </div>
        
        ${
          geminiAnalysis?.keyFindings?.length > 0
            ? `
        <div class="section">
            <div class="section-title">🔍 Key Findings</div>
            <div class="findings-grid">
                <div class="finding-item">
                    <div class="finding-title">Primary Observations</div>
                    <ul class="finding-list">
                        ${geminiAnalysis.keyFindings
                          .slice(
                            0,
                            Math.ceil(geminiAnalysis.keyFindings.length / 2)
                          )
                          .map((finding) => `<li>${finding}</li>`)
                          .join("")}
                    </ul>
                </div>
                <div class="finding-item">
                    <div class="finding-title">Additional Findings</div>
                    <ul class="finding-list">
                        ${geminiAnalysis.keyFindings
                          .slice(
                            Math.ceil(geminiAnalysis.keyFindings.length / 2)
                          )
                          .map((finding) => `<li>${finding}</li>`)
                          .join("")}
                    </ul>
                </div>
            </div>
        </div>
        `
            : ""
        }
        
        <div class="section">
            <div class="section-title">📋 Care Recommendations</div>
            <div class="recommendations-grid">
                ${
                  geminiAnalysis?.recommendations?.homecure?.length > 0
                    ? `
                <div class="rec-box rec-homecure">
                    <div class="rec-title">🏠 Home Care Solutions</div>
                    <ul class="rec-list">
                        ${geminiAnalysis.recommendations.homecure
                          .slice(0, 4)
                          .map((rec) => `<li>${rec}</li>`)
                          .join("")}
                    </ul>
                </div>
                `
                    : ""
                }
                
                ${
                  geminiAnalysis?.recommendations?.immediate?.length > 0
                    ? `
                <div class="rec-box rec-immediate">
                    <div class="rec-title">⚡ Immediate Actions</div>
                    <ul class="rec-list">
                        ${geminiAnalysis.recommendations.immediate
                          .slice(0, 4)
                          .map((rec) => `<li>${rec}</li>`)
                          .join("")}
                    </ul>
                </div>
                `
                    : ""
                }
                
                ${
                  geminiAnalysis?.recommendations?.shortTerm?.length > 0
                    ? `
                <div class="rec-box rec-shortterm">
                    <div class="rec-title">📅 Short-term Care</div>
                    <ul class="rec-list">
                        ${geminiAnalysis.recommendations.shortTerm
                          .slice(0, 4)
                          .map((rec) => `<li>${rec}</li>`)
                          .join("")}
                    </ul>
                </div>
                `
                    : ""
                }
                
                ${
                  geminiAnalysis?.recommendations?.longTerm?.length > 0
                    ? `
                <div class="rec-box rec-longterm">
                    <div class="rec-title">🌱 Long-term Care</div>
                    <ul class="rec-list">
                        ${geminiAnalysis.recommendations.longTerm
                          .slice(0, 4)
                          .map((rec) => `<li>${rec}</li>`)
                          .join("")}
                    </ul>
                </div>
                `
                    : ""
                }
            </div>
        </div>
        
        <div class="section">
            <div class="section-title">⚠️ Important Information</div>
            <div class="warning-section">
                ${
                  geminiAnalysis?.warningSignsToWatch?.length > 0
                    ? `
                <div class="warning-box">
                    <div class="warning-title">Warning Signs to Monitor</div>
                    <ul class="warning-list">
                        ${geminiAnalysis.warningSignsToWatch
                          .slice(0, 5)
                          .map((warning) => `<li>${warning}</li>`)
                          .join("")}
                    </ul>
                </div>
                `
                    : ""
                }
                
                <div class="vet-recommendation">
                    <div class="vet-rec-title">🏥 Veterinary Recommendation</div>
                    <div class="vet-rec-text">
                        ${
                          geminiAnalysis?.veterinaryVisit === "immediate"
                            ? "🚨 Seek immediate veterinary attention"
                            : geminiAnalysis?.veterinaryVisit === "within_days"
                            ? "⏰ Schedule veterinary visit within days"
                            : geminiAnalysis?.veterinaryVisit === "routine"
                            ? "📅 Schedule routine check-up"
                            : "✅ Continue monitoring at home"
                        }
                    </div>
                </div>
            </div>
        </div>
        
        
        
        <div class="disclaimer">
            <div class="disclaimer-text">
                <strong>⚠️ IMPORTANT DISCLAIMER:</strong> This AI analysis is for informational purposes only and does not replace professional veterinary care. Always consult with a qualified veterinarian for accurate diagnosis and treatment. The AI assessment is based on provided symptoms and should be used as supplementary information only.
            </div>
        </div>
        
        <div class="footer">
            <p><strong>VetSense AI</strong> • Advanced Pet Health Analysis System</p>
            <p>Report generated on ${currentDate} | For emergencies, contact your nearest animal hospital.</p>
        </div>
    </div>
    
    <script>
        // Auto-print when page loads
        window.onload = function() {
            window.print();
        };
        
        // Close window after printing
        window.onafterprint = function() {
            window.close();
        };
    </script>
</body>
</html>`;
  };

  // Function to open PDF in new tab and trigger download
  const openPDFReport = () => {
    const pdfContent = generatePDFContent();
    const newWindow = window.open("", "_blank");

    if (newWindow) {
      newWindow.document.write(pdfContent);
      newWindow.document.close();

      // Set the document title
      newWindow.document.title = `VetSense AI Report - ${
        formData?.petName || "Pet"
      } - ${new Date().toLocaleDateString()}`;

      // Focus the new window
      newWindow.focus();
    } else {
    }
  };

  // Auto-execute PDF generation when component mounts
  React.useEffect(() => {
    openPDFReport();
    onClose(); // Close immediately after opening
  }, []);

  return null; // No UI needed since we're just generating the PDF
};

export default PetHealthReportPDF;
